import React, { useMemo } from 'react'
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { Box } from '@mui/material'

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
)

const DIAS_POR_MES = 30.4375

const parseData = (dt) => {
  if (!dt) return null
  if (typeof dt === 'string') {
    const partes = dt.split(' ')[0]
    if (partes.includes('/')) {
      const [dia, mes, ano] = partes.split('/')
      const d = new Date(+ano, +mes - 1, +dia)
      return Number.isNaN(d.getTime()) ? null : d
    }
    const m = partes.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (m) {
      const d = new Date(+m[1], +m[2] - 1, +m[3])
      return Number.isNaN(d.getTime()) ? null : d
    }
  }
  const d = new Date(dt)
  return Number.isNaN(d.getTime()) ? null : d
}

const calcularDias = (dtNascimento, dtAvaliacao) => {
  const nasc = parseData(dtNascimento)
  const aval = parseData(dtAvaliacao)
  if (!nasc || !aval) return null
  const diff = aval.getTime() - nasc.getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24))
}

const pontoRef = (linha, campo) => ({
  x: linha.nr_idade_meses,
  y: linha[campo],
})

const montarDatasetRef = (label, valores, corBorda, corFill, alvoFill) => ({
  label,
  data: valores,
  borderColor: corBorda,
  backgroundColor: corFill,
  borderWidth: 1,
  borderDash: [4, 4],
  pointRadius: 0,
  tension: 0.4,
  fill: alvoFill,
})

const CONFIG_LINHAS = {
  Z: [
    {
      label: '+3 DP',
      campo: 'nr_desvio_mais_3',
      borda: 'rgba(244, 67, 54, 0.5)',
      fill: 'rgba(255, 152, 0, 0.12)',
      alvo: 1,
    },
    {
      label: '+2 DP',
      campo: 'nr_desvio_mais_2',
      borda: 'rgba(255, 152, 0, 0.6)',
      fill: 'rgba(255, 235, 59, 0.18)',
      alvo: 2,
    },
    {
      label: '+1 DP',
      campo: 'nr_desvio_mais_1',
      borda: 'rgba(170, 170, 170, 0.5)',
      fill: 'rgba(76, 175, 80, 0.14)',
      alvo: 3,
    },
    {
      label: '-1 DP',
      campo: 'nr_desvio_menos_1',
      borda: 'rgba(170, 170, 170, 0.5)',
      fill: 'rgba(255, 235, 59, 0.18)',
      alvo: 4,
    },
    {
      label: '-2 DP',
      campo: 'nr_desvio_menos_2',
      borda: 'rgba(255, 152, 0, 0.6)',
      fill: 'rgba(244, 67, 54, 0.16)',
      alvo: 5,
    },
    {
      label: '-3 DP',
      campo: 'nr_desvio_menos_3',
      borda: 'rgba(244, 67, 54, 0.5)',
      fill: 'transparent',
      alvo: false,
    },
  ],
  P: [
    {
      label: 'P97',
      campo: 'nr_percentil_97',
      borda: 'rgba(244, 67, 54, 0.5)',
      fill: 'rgba(255, 152, 0, 0.12)',
      alvo: 1,
    },
    {
      label: 'P85',
      campo: 'nr_percentil_85',
      borda: 'rgba(255, 152, 0, 0.6)',
      fill: 'rgba(76, 175, 80, 0.14)',
      alvo: 3,
    },
    {
      label: 'P15',
      campo: 'nr_percentil_15',
      borda: 'rgba(170, 170, 170, 0.5)',
      fill: 'rgba(255, 235, 59, 0.18)',
      alvo: 4,
    },
    {
      label: 'P3',
      campo: 'nr_percentil_3',
      borda: 'rgba(244, 67, 54, 0.5)',
      fill: 'transparent',
      alvo: false,
    },
  ],
}

const CAMPO_MEDIANA = {
  Z: 'nr_mediana',
  P: 'nr_percentil_50',
}

const CAMPOS_VALORES = {
  Z: [
    'nr_desvio_menos_3',
    'nr_desvio_menos_2',
    'nr_desvio_menos_1',
    'nr_mediana',
    'nr_desvio_mais_1',
    'nr_desvio_mais_2',
    'nr_desvio_mais_3',
  ],
  P: [
    'nr_percentil_3',
    'nr_percentil_15',
    'nr_percentil_50',
    'nr_percentil_85',
    'nr_percentil_97',
  ],
}

const LABEL_MEDIANA = {
  Z: 'Mediana',
  P: 'P50',
}

const classificar = (valor, ref, tpCurva) => {
  if (!ref) return '-'
  if (tpCurva === 'P') {
    if (valor < ref.nr_percentil_3) return 'Muito baixo (< P3)'
    if (valor < ref.nr_percentil_15) return 'Baixo (P3 a P15)'
    if (valor <= ref.nr_percentil_85) return 'Adequado (P15 a P85)'
    if (valor <= ref.nr_percentil_97) return 'Elevado (P85 a P97)'
    return 'Muito elevado (> P97)'
  }
  if (valor < ref.nr_desvio_menos_3) return 'Muito baixo (< -3 DP)'
  if (valor < ref.nr_desvio_menos_2) return 'Baixo (-3 a -2 DP)'
  if (valor <= ref.nr_desvio_mais_2) return 'Adequado (-2 a +2 DP)'
  if (valor <= ref.nr_desvio_mais_3) return 'Elevado (+2 a +3 DP)'
  return 'Muito elevado (> +3 DP)'
}

const CurvaCrescimentoOMS = ({
  valores = [],
  paciente = [],
  campoPaciente = 'nr_peso',
  tpCurva = 'Z',
  titulo = 'Curva de Crescimento',
  unidade = 'kg',
  dtNascimento = null,
  refsPorMedicao = {},
}) => {
  const dadosPaciente = useMemo(() => {
    const minRef = valores.length
      ? Math.min(...valores.map((v) => v.nr_idade_meses || 0))
      : null
    const maxRef = valores.length
      ? Math.max(...valores.map((v) => v.nr_idade_meses || 0))
      : null
    return paciente
      .map((p) => {
        const idadeDias =
          p.nr_idade_dias ?? calcularDias(dtNascimento, p.dt_avaliacao)
        const x =
          idadeDias == null ? null : +(idadeDias / DIAS_POR_MES).toFixed(2)
        const y = p[campoPaciente]
        const ref = refsPorMedicao?.[p.id_curva_crescimento_paciente] ?? null
        return { x, y, linha: p, ref }
      })
      .filter(
        (p) =>
          p.y != null &&
          p.x != null &&
          (minRef == null || (p.x >= minRef && p.x <= maxRef)),
      )
      .sort((a, b) => a.x - b.x)
  }, [paciente, campoPaciente, dtNascimento, refsPorMedicao, valores])

  const data = useMemo(() => {
    const linhas = CONFIG_LINHAS[tpCurva]
    const datasets = linhas.map((ln) =>
      montarDatasetRef(
        ln.label,
        valores.map((v) => pontoRef(v, ln.campo)),
        ln.borda,
        ln.fill,
        ln.alvo,
      ),
    )

    datasets.push({
      label: LABEL_MEDIANA[tpCurva],
      data: valores.map((v) => pontoRef(v, CAMPO_MEDIANA[tpCurva])),
      borderColor: '#2e7d32',
      backgroundColor: '#2e7d32',
      borderWidth: 2.5,
      pointRadius: 0,
      tension: 0.4,
      fill: false,
    })

    datasets.push({
      label: 'Paciente',
      data: dadosPaciente,
      borderColor: '#1976d2',
      backgroundColor: '#1976d2',
      borderWidth: 2.5,
      pointRadius: 5,
      pointHoverRadius: 7,
      tension: 0.3,
      fill: false,
    })

    return { datasets }
  }, [valores, dadosPaciente, tpCurva])

  const idadeMaxima = useMemo(() => {
    if (valores.length) {
      return Math.max(...valores.map((v) => v.nr_idade_meses || 0))
    }
    const idadePaciente = dadosPaciente.length
      ? Math.ceil(dadosPaciente[dadosPaciente.length - 1].x)
      : 0
    return Math.max(idadePaciente, 24)
  }, [dadosPaciente, valores])

  const idadeMinima = useMemo(() => {
    if (valores.length) {
      return Math.min(...valores.map((v) => v.nr_idade_meses || 0))
    }
    return 0
  }, [valores])

  const maxReferencia = useMemo(() => {
    let max = 0
    const campos = CAMPOS_VALORES[tpCurva] || []
    valores.forEach((v) => {
      campos.forEach((c) => {
        const n = v[c]
        if (typeof n === 'number' && Number.isFinite(n) && n > max) max = n
      })
    })
    return max
  }, [valores, tpCurva])

  const minPaciente = useMemo(
    () =>
      dadosPaciente.reduce(
        (min, p) => (typeof p.y === 'number' && p.y < min ? p.y : min),
        0,
      ),
    [dadosPaciente],
  )

  const maxPaciente = useMemo(
    () =>
      dadosPaciente.reduce(
        (max, p) => (typeof p.y === 'number' && p.y > max ? p.y : max),
        0,
      ),
    [dadosPaciente],
  )

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'nearest', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            filter: (item) =>
              [LABEL_MEDIANA[tpCurva], 'Paciente'].includes(item.text),
          },
        },
        title: {
          display: true,
          text: titulo,
          font: { size: 16, weight: 'bold' },
        },
        tooltip: {
          callbacks: {
            title: (items) => {
              const mes = items[0].parsed.x
              return `Idade: ${
                Number.isInteger(mes) ? mes : mes.toFixed(1)
              } meses`
            },
            label: (item) => {
              const val = item.parsed.y
              if (item.dataset.label === 'Paciente') {
                const ponto = dadosPaciente[item.dataIndex]
                const ref = ponto?.ref ?? null
                const classe = classificar(val, ref, tpCurva)
                const campoMed = CAMPO_MEDIANA[tpCurva]
                const mediana = ref ? ref[campoMed] : '-'
                return [
                  `Paciente: ${val} ${unidade}`,
                  `Referência OMS: ${mediana} ${unidade}`,
                  `Classificação: ${classe}`,
                ]
              }
              return `${item.dataset.label}: ${val} ${unidade}`
            },
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          title: { display: true, text: 'Idade (meses)' },
          min: idadeMinima,
          max: idadeMaxima,
          ticks: { stepSize: 1, autoSkip: false },
        },
        y: {
          title: { display: true, text: unidade },
          beginAtZero: true,
          min: Math.min(0, minPaciente),
          max:
            Math.max(maxReferencia, maxPaciente) > 0
              ? +(Math.max(maxReferencia, maxPaciente) * 1.1).toFixed(2)
              : undefined,
        },
      },
    }),
    [
      titulo,
      unidade,
      tpCurva,
      idadeMaxima,
      idadeMinima,
      maxReferencia,
      minPaciente,
      maxPaciente,
      dadosPaciente,
    ],
  )

  return (
    <div style={{ width: '100%', height: 600 }}>
      {dadosPaciente.length ? (
        <Line data={data} options={options} />
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'text.secondary',
          }}
        >
          Nenhuma medição salva para este paciente.
        </Box>
      )}
    </div>
  )
}

export default CurvaCrescimentoOMS
